/**
 * YouTube Analytics Service
 * Frontend service for interacting with YouTube Analytics API endpoints
 */

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

const V1_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : 'http://localhost:7777/api/v1';

export interface RetentionDataPoint {
  percent: number;
  ratio: number;
  absoluteRetention: number; // Percentage of viewers still watching
  relativeRetention: number; // Performance vs similar videos (0-1, 0.5 = average)
  timestampSeconds?: number;
}

export interface VideoStats {
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  subscribersGained: number | null;
  subscribersLost: number | null;
  estimatedMinutesWatched: number | null;
  averageViewDuration: number | null; // in seconds
  averageViewPercentage: number | null;
  videoTitle: string | null;
  videoPublishedAt: string | null;
}

export interface RetentionData {
  sessionId: string;
  videoId: string;
  retentionData: RetentionDataPoint[];
  videoDurationSeconds: number | null;
  videoStats?: VideoStats;
  fetchedAt: string;
  fetchCount?: number;
  cached?: boolean;
}

export interface YouTubeAuthStatus {
  authenticated: boolean;
  tokenExpiry: string | null;
  scopes: string[];
  canEditVideos: boolean;
  canPostComments: boolean;
  canReadVideoMetadata: boolean;
}

/**
 * Check if the current user has YouTube Analytics credentials
 */
export async function getAuthStatus(token: string): Promise<YouTubeAuthStatus> {
  const response = await fetch(`${V1_API_BASE_URL}/youtube/auth/status`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to check auth status');
  }

  return response.json();
}

/**
 * Get OAuth authorization URL for YouTube Analytics
 * @param token - Auth token
 * @param returnUrl - URL to return to after OAuth (optional, defaults to current page)
 */
export async function getAuthUrl(token: string, returnUrl?: string): Promise<string> {
  const params = new URLSearchParams();
  if (returnUrl) {
    params.set('returnUrl', returnUrl);
  }

  const url = `${V1_API_BASE_URL}/youtube/auth/url${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get auth URL');
  }

  const data = await response.json();
  return data.authUrl;
}

/**
 * Disconnect YouTube Analytics
 */
export async function disconnectYouTube(token: string): Promise<void> {
  const response = await fetch(`${V1_API_BASE_URL}/youtube/auth`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to disconnect YouTube');
  }
}

/**
 * Safely parse JSON response, handling HTML error pages
 */
async function safeParseJson(response: Response): Promise<any> {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    // If response is HTML, extract meaningful error info
    if (text.trim().startsWith('<')) {
      // Check for common HTTP error codes in HTML
      if (response.status === 502 || response.status === 503 || response.status === 504) {
        throw new Error(`Service temporarily unavailable (${response.status}). Please try again in a few moments.`);
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication error. Please try logging in again.');
      }
      throw new Error(`Server error (${response.status}). The service may be restarting - please try again.`);
    }
    throw new Error(text || `Request failed with status ${response.status}`);
  }
}

/**
 * Get retention data for a session
 */
export async function getRetentionData(
  sessionId: string,
  token?: string,
  shareToken?: string
): Promise<RetentionData | null> {
  let url: string;
  const headers: Record<string, string> = {};

  if (shareToken) {
    // Public share access
    url = `${API_BASE_URL}/public/share/${shareToken}/retention`;
  } else {
    // Authenticated access
    url = `${API_BASE_URL}/youtube-analytics/retention/${sessionId}`;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  let response: Response;

  try {
    response = await fetch(url, { headers });
  } catch (networkError) {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await safeParseJson(response);
    throw new Error(error.error || error.message || 'Failed to get retention data');
  }

  const data = await safeParseJson(response);

  // Handle public share response format
  if (shareToken && data.retentionData === null) {
    return null;
  }

  return data;
}

/**
 * Fetch fresh retention data for a session
 */
export async function fetchRetentionData(
  sessionId: string,
  youtubeUrl: string,
  token: string,
  forceRefresh: boolean = false
): Promise<RetentionData> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/youtube-analytics/retention/${sessionId}/fetch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ youtubeUrl, forceRefresh })
    });
  } catch (networkError) {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (!response.ok) {
    const error = await safeParseJson(response);

    if (error.needsAuth) {
      throw new Error('NEEDS_AUTH:' + error.message);
    }

    throw new Error(error.error || error.message || 'Failed to fetch retention data');
  }

  return safeParseJson(response);
}

/**
 * Check if retention data needs authentication to fetch
 */
export function needsAuthentication(error: Error): boolean {
  return error.message.startsWith('NEEDS_AUTH:');
}

/**
 * Get the error message without the NEEDS_AUTH prefix
 */
export function getErrorMessage(error: Error): string {
  if (error.message.startsWith('NEEDS_AUTH:')) {
    return error.message.substring('NEEDS_AUTH:'.length);
  }
  return error.message;
}
